import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGamerAuth } from './GamerAuthContext';

export default function GamerAuthModal({ isOpen, onClose, onSuccess }) {
    const { loginWithCode } = useGamerAuth();
    const [gamerCode, setGamerCode] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!gamerCode.trim()) return;

        setIsProcessing(true);
        setError(false);
        try {
            await loginWithCode(gamerCode.trim());
            onSuccess(); // triggers the checkout to resume
            onClose();
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setIsProcessing(false);
        }
    };

    if (typeof window === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/80 backdrop-blur-md"
                >
                    <motion.div 
                        initial={{ scale: 0.9, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.9, y: 20 }}
                        className="bg-[#111111] border border-pink-500/50 p-8 rounded-2xl shadow-[0_10px_40px_rgba(236,72,153,0.3)] max-w-sm w-full mx-4"
                    >
                        <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-2 text-center tracking-wider">GAMER LOGIN</h3>
                        <p className="text-sm text-gray-400 text-center mb-6">Enter your unique GAMER Code to proceed with checkout and save your games.</p>
                        
                        <form onSubmit={handleSubmit}>
                            <input 
                                type="text" 
                                autoFocus
                                value={gamerCode}
                                onChange={(e) => setGamerCode(e.target.value.toUpperCase())}
                                placeholder="GAMER CODE"
                                className={`w-full bg-black border ${error ? 'border-red-500 text-red-500' : 'border-[#333] text-white focus:border-pink-500'} rounded-lg px-4 py-3 text-center text-xl tracking-widest outline-none transition-colors uppercase`}
                            />
                            {error && <p className="text-red-500 text-xs text-center mt-2">Invalid code or network error.</p>}
                            <div className="flex gap-3 mt-6">
                                <button type="button" onClick={onClose} disabled={isProcessing} className="flex-1 py-3 rounded-lg bg-[#222] text-white font-medium hover:bg-[#333] transition-colors disabled:opacity-50">Cancel</button>
                                <button type="submit" disabled={isProcessing || !gamerCode.trim()} className="flex-1 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:opacity-90 transition-opacity shadow-[0_0_15px_rgba(219,39,119,0.3)] disabled:opacity-50">
                                    {isProcessing ? 'Verifying...' : 'Login'}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}
